import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

// Import section components
import Header from './Header';
import HeroSection from './HeroSection';
import ServicesSection from './ServicesSection';
import TestimonialsSection from './TestimonialsSection';
import RecentPosts from './RecentPosts';
import CTASection from './CTASection';
import Footer from './Footer';


// Import data
import { services, testimonials, stats } from '../data/landingPageData';

// Browser detection
const isChromium = () => {
  return /Chrome|Chromium|Edge/.test(navigator.userAgent) && !/Firefox/.test(navigator.userAgent);
};

const OptimizedLandingPage = ({ isMenuOpen, setIsMenuOpen }) => {
  const [isChromiumBrowser] = useState(isChromium());

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <Header isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      
      <HeroSection stats={stats} />
      
      <ServicesSection
        services={services}
        OptimizedSpotlightGroup={OptimizedSpotlightGroup}
        OptimizedGradientCard={OptimizedGradientCard}
        isChromiumBrowser={isChromiumBrowser}
      />
      
      <TestimonialsSection
        testimonials={testimonials}
        services={services}
        OptimizedSpotlightGroup={OptimizedSpotlightGroup}
        OptimizedGradientCard={OptimizedGradientCard}
        isChromiumBrowser={isChromiumBrowser}
      />

      <RecentPosts />
      
      <CTASection />
      
      <Footer />
    </div>
  );
};

// Highly Optimized Spotlight Group Component
const OptimizedSpotlightGroup = React.memo(({ children, isChromiumBrowser = true }) => {
  const containerRef = useRef(null);
  const cardsCache = useRef([]);
  const rafRef = useRef();
  const lastMousePosition = useRef({ x: 0, y: 0 });

  // Throttled mouse move handler optimized for 60fps
  const handleMouseMove = useCallback((e) => {
    if (rafRef.current) return;

    rafRef.current = requestAnimationFrame(() => {
      const container = containerRef.current;
      if (!container) {
        rafRef.current = null;
        return;
      }

      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Only update if movement is significant (reduces unnecessary updates)
      const deltaX = Math.abs(x - lastMousePosition.current.x);
      const deltaY = Math.abs(y - lastMousePosition.current.y);
      
      if (deltaX < 2 && deltaY < 2) {
        rafRef.current = null;
        return;
      }

      lastMousePosition.current = { x, y };

      // Use cached cards array to avoid DOM queries
      cardsCache.current.forEach((card) => {
        if (!card || !card.isConnected) return;

        const cardRect = card.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const cardX = cardRect.left - containerRect.left;
        const cardY = cardRect.top - containerRect.top;
        
        const relativeX = x - cardX;
        const relativeY = y - cardY;
        
        // Use CSS custom properties for better performance
        card.style.setProperty('--mouse-x', `${relativeX}px`);
        card.style.setProperty('--mouse-y', `${relativeY}px`);
      });

      rafRef.current = null;
    });
  }, []);

  // Mouse enter handler to set hover state
  const handleMouseEnter = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    
    container.classList.add('spotlight-group-hover');
  }, []);

  // Optimized mouse leave handler
  const handleMouseLeave = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    // Remove hover class immediately to trigger opacity reset
    container.classList.remove('spotlight-group-hover');
    
    // Reset mouse positions to center for smooth exit and force immediate cleanup
    cardsCache.current.forEach((card) => {
      if (!card || !card.isConnected) return;
      
      const rect = card.getBoundingClientRect();
      card.style.setProperty('--mouse-x', `${rect.width / 2}px`);
      card.style.setProperty('--mouse-y', `${rect.height / 2}px`);
      
      // Remove individual hover states immediately
      card.classList.remove('spotlight-card-hover');
    });
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Cache spotlight cards on mount and when children change
    const updateCardsCache = () => {
      cardsCache.current = Array.from(container.querySelectorAll('.spotlight-card'));
    };

    updateCardsCache();
    container.classList.add(isChromiumBrowser ? 'spotlight-group-optimized' : 'spotlight-group');

    // Use passive listeners for better scroll performance
    const options = { passive: true };
    container.addEventListener('mouseenter', handleMouseEnter, options);
    container.addEventListener('mousemove', handleMouseMove, options);
    container.addEventListener('mouseleave', handleMouseLeave, options);

    // Update cache when DOM changes
    const observer = new MutationObserver(updateCardsCache);
    observer.observe(container, { childList: true, subtree: true });

    return () => {
      container.removeEventListener('mouseenter', handleMouseEnter);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseleave', handleMouseLeave);
      container.classList.remove('spotlight-group', 'spotlight-group-optimized');
      observer.disconnect();
      
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [handleMouseEnter, handleMouseMove, handleMouseLeave, isChromiumBrowser]);

  return (
    <div ref={containerRef} className={isChromiumBrowser ? 'spotlight-group-optimized' : 'spotlight-group'}>
      {children}
    </div>
  );
});

// Static Gradient Card - No animations
const OptimizedGradientCard = React.memo(({
  children,
  index,
  isChromiumBrowser = true
}) => {
  const baseClass = isChromiumBrowser ? 'gradient-border-optimized' : 'gradient-border';
  
  return (
    <div className={`${baseClass} rounded-3xl`}>
      {children}
    </div>
  );
});

export default OptimizedLandingPage;
export { OptimizedSpotlightGroup, OptimizedGradientCard };